import type { MessageKind, MessageView } from "@walkie/protocol"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { agentColor, APP_SHORT } from "../lib/agents"
import { isWithdrawn, oneLine } from "../lib/messages"
import { shortId } from "../lib/refs"
import { age } from "../lib/time"
import { sendMessage } from "../store/actions"
import { useStore } from "../store/store"
import { KindBadge, PersonSwatch, Popover, StateDot, Swatch } from "./ui"

const KINDS: { kind: MessageKind; label: string }[] = [
	{ kind: "message", label: "Message" },
	{ kind: "question", label: "Question" },
	{ kind: "blocker", label: "Blocker" },
	{ kind: "handoff", label: "Handoff" },
	{ kind: "decision", label: "Decision" },
	{ kind: "heads_up", label: "Heads-up" },
]

interface Candidate {
	handle: string
	type: "agent" | "person"
	id: string
	sub: string
}

function useCandidates(): Candidate[] {
	const agents = useStore(s => s.agents)
	const people = useStore(s => s.people)
	return useMemo(() => {
		const rank = { active: 0, idle: 1, offline: 2, ended: 3 }
		const as = Object.values(agents)
			.filter(a => a.shared && a.state !== "ended")
			.sort((a, b) => rank[a.state] - rank[b.state] || a.handle.localeCompare(b.handle))
			.map(a => ({
				handle: a.handle,
				type: "agent" as const,
				id: a.id,
				sub: `${a.ownerHandle} · ${APP_SHORT[a.app]}`,
			}))
		const ps = people.map(p => ({
			handle: p.handle,
			type: "person" as const,
			id: p.userId,
			sub: p.name,
		}))
		return [...as, ...ps]
	}, [agents, people])
}

export function Composer(props: {
	target: "main" | "thread"
	roomId: string | null
	replyTo?: string
	placeholder: string
	/** When replying to an open request, offer to resolve it with the reply. */
	resolvable?: boolean
}) {
	const [kind, setKind] = useState<MessageKind>("message")
	const [title, setTitle] = useState("")
	const [body, setBody] = useState("")
	const [to, setTo] = useState<string[]>([])
	const [toQuery, setToQuery] = useState("")
	const [urgent, setUrgent] = useState(false)
	const [resolve, setResolve] = useState(false)
	const [cites, setCites] = useState<string[]>([])
	const [supersedes, setSupersedes] = useState<string | null>(null)
	const [picker, setPicker] = useState<{
		mode: "cite" | "supersede"
		anchor: HTMLElement
	} | null>(null)
	const [ac, setAc] = useState<{
		source: "to" | "body"
		query: string
		index: number
	} | null>(null)
	const ta = useRef<HTMLTextAreaElement>(null)
	const toInput = useRef<HTMLInputElement>(null)
	const candidates = useCandidates()
	const agents = useStore(s => s.agents)
	const canWrite = useStore(s => s.role !== "viewer")
	const focusReq = useStore(s => s.ui.focusComposer)
	const citeAdd = useStore(s => s.ui.citeAdd)
	const messages = useStore(s => s.messages)

	useEffect(() => {
		if (!citeAdd || citeAdd.target !== props.target) return
		setCites(c => (c.includes(citeAdd.id) || c.length >= 10 ? c : [...c, citeAdd.id]))
	}, [citeAdd, props.target])

	useEffect(() => {
		if (focusReq.tick && focusReq.target === props.target) ta.current?.focus()
	}, [focusReq, props.target])

	useLayoutEffect(() => {
		const el = ta.current
		if (!el) return
		el.style.height = "auto"
		el.style.height = Math.min(200, el.scrollHeight) + "px"
	}, [body])

	const matches = useMemo(() => {
		if (!ac) return []
		const q = ac.query.toLowerCase()
		return candidates
			.filter(
				c =>
					(ac.source !== "to" || !to.includes(c.handle)) &&
					(c.handle.includes(q) || c.sub.toLowerCase().includes(q)),
			)
			.sort((a, b) => Number(!a.handle.startsWith(q)) - Number(!b.handle.startsWith(q)))
			.slice(0, 8)
	}, [ac, candidates, to])

	const pick = (c: Candidate) => {
		if (!ac) return
		if (ac.source === "to") {
			setTo(t => [...t, c.handle])
			setToQuery("")
			toInput.current?.focus()
		} else {
			const el = ta.current
			const caret = el?.selectionStart ?? body.length
			const before = body.slice(0, caret).replace(/@([a-z0-9-]*)$/i, `@${c.handle} `)
			const next = before + body.slice(caret)
			setBody(next)
			requestAnimationFrame(() => {
				el?.focus()
				el?.setSelectionRange(before.length, before.length)
			})
		}
		setAc(null)
	}

	const acKeys = (e: React.KeyboardEvent) => {
		if (!ac || !matches.length) return false
		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			const d = e.key === "ArrowDown" ? 1 : -1
			setAc({ ...ac, index: (ac.index + d + matches.length) % matches.length })
			e.preventDefault()
			return true
		}
		if (e.key === "Enter" || e.key === "Tab") {
			const c = matches[ac.index]
			if (c) pick(c)
			e.preventDefault()
			return true
		}
		if (e.key === "Escape") {
			setAc(null)
			e.preventDefault()
			e.stopPropagation()
			return true
		}
		return false
	}

	const submit = () => {
		const text = body.trim()
		if (!text || !props.roomId) return
		void sendMessage({
			roomId: props.roomId,
			replyTo: props.replyTo,
			kind,
			title: title.trim() || undefined,
			body: text,
			to,
			priority: urgent ? "urgent" : "normal",
			resolve: resolve ? { resolution: text.slice(0, 2000) } : undefined,
			cites,
			supersedes: kind === "decision" && supersedes ? supersedes : undefined,
		})
		setBody("")
		setTitle("")
		setResolve(false)
		setCites([])
		setSupersedes(null)
		if (props.target === "main") {
			setTo([])
			setUrgent(false)
			setKind("message")
		}
	}

	if (!canWrite)
		return (
			<div className="composer">
				<div className="dim" style={{ padding: "4px 2px" }}>
					You have view access in this project.
				</div>
			</div>
		)

	const showTitle = kind !== "message" && kind !== "heads_up"
	const listId = `ac-${props.target}`

	return (
		<div className="composer">
			{picker && (
				<MessagePicker
					anchor={picker.anchor}
					mode={picker.mode}
					exclude={picker.mode === "cite" ? cites : []}
					onClose={() => setPicker(null)}
					onPick={id => {
						if (picker.mode === "cite")
							setCites(c => (c.includes(id) ? c : [...c, id].slice(0, 10)))
						else setSupersedes(id)
						setPicker(null)
						ta.current?.focus()
					}}
				/>
			)}
			{ac && matches.length > 0 && (
				<div
					className="autocomplete"
					id={listId}
					role="listbox"
					style={{ bottom: "100%", left: 8, marginBottom: 4 }}
				>
					{matches.map((c, i) => {
						const a = c.type === "agent" ? agents[c.id] : undefined
						return (
							<button
								key={c.type + c.id}
								className="ac-item"
								role="option"
								aria-selected={i === ac.index}
								onMouseDown={e => {
									e.preventDefault()
									pick(c)
								}}
							>
								{a ? <Swatch agent={a} /> : <PersonSwatch handle={c.handle} />}
								<b style={{ color: a ? agentColor(a.color) : undefined }}>@{c.handle}</b>
								<span className="dim ell grow">{c.sub}</span>
								{a && <StateDot state={a.state} />}
							</button>
						)
					})}
				</div>
			)}
			<div className={"composer-box" + (urgent ? " urgent" : "")}>
				{showTitle && (
					<input
						className="title-input"
						value={title}
						onChange={e => setTitle(e.target.value)}
						placeholder={`${KINDS.find(k => k.kind === kind)?.label} title (optional)`}
						aria-label="Title"
						maxLength={200}
					/>
				)}
				{(cites.length > 0 || (kind === "decision" && supersedes)) && (
					<div className="cite-chips" aria-label="Cited messages">
						{kind === "decision" && supersedes && (
							<RefChip
								m={messages[supersedes]}
								id={supersedes}
								prefix="supersedes"
								onRemove={() => setSupersedes(null)}
							/>
						)}
						{cites.map(id => (
							<RefChip
								key={id}
								m={messages[id]}
								id={id}
								prefix="cites"
								onRemove={() => setCites(c => c.filter(x => x !== id))}
							/>
						))}
					</div>
				)}
				<textarea
					ref={ta}
					rows={1}
					value={body}
					placeholder={props.placeholder}
					aria-label={props.placeholder}
					aria-autocomplete="list"
					aria-controls={ac?.source === "body" ? listId : undefined}
					onChange={e => {
						setBody(e.target.value)
						const caret = e.target.selectionStart ?? e.target.value.length
						const m = e.target.value.slice(0, caret).match(/(?:^|\s)@([a-z0-9-]*)$/i)
						setAc(m ? { source: "body", query: m[1] ?? "", index: 0 } : null)
					}}
					onKeyDown={e => {
						if (ac?.source === "body" && acKeys(e)) return
						if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
							e.preventDefault()
							submit()
						} else if (e.key === "Escape") {
							e.currentTarget.blur()
						}
					}}
					onBlur={() =>
						setTimeout(() => setAc(a => (a?.source === "body" ? null : a)), 120)
					}
				/>
				<div className="composer-bar">
					<select
						className="select kind-select"
						value={kind}
						onChange={e => setKind(e.target.value as MessageKind)}
						aria-label="Message kind"
					>
						{KINDS.map(k => (
							<option key={k.kind} value={k.kind}>
								{k.label}
							</option>
						))}
					</select>
					<div className="to-field" onClick={() => toInput.current?.focus()}>
						<span className="dim" style={{ fontSize: 11 }}>
							to
						</span>
						{to.map(h => {
							const c = candidates.find(x => x.handle === h)
							const a = c?.type === "agent" ? agents[c.id] : undefined
							return (
								<span
									key={h}
									className="to-chip"
									style={{ color: a ? agentColor(a.color) : undefined }}
								>
									@{h}
									<button
										aria-label={`Remove @${h}`}
										onClick={() => setTo(t => t.filter(x => x !== h))}
									>
										×
									</button>
								</span>
							)
						})}
						<input
							ref={toInput}
							value={toQuery}
							placeholder={to.length ? "" : "@handle (optional)"}
							aria-label="Recipients"
							aria-autocomplete="list"
							aria-controls={ac?.source === "to" ? listId : undefined}
							onChange={e => {
								const v = e.target.value.replace(/^@/, "")
								setToQuery(v)
								setAc({ source: "to", query: v, index: 0 })
							}}
							onFocus={() => setAc({ source: "to", query: toQuery, index: 0 })}
							onBlur={() =>
								setTimeout(() => setAc(a => (a?.source === "to" ? null : a)), 120)
							}
							onKeyDown={e => {
								if (ac?.source === "to" && acKeys(e)) return
								if (e.key === "Backspace" && !toQuery && to.length)
									setTo(t => t.slice(0, -1))
								if (e.key === "Enter") {
									e.preventDefault()
									ta.current?.focus()
								}
							}}
						/>
					</div>
					{props.resolvable && (
						<label className="check" style={{ fontSize: 11 }}>
							<input
								type="checkbox"
								checked={resolve}
								onChange={e => setResolve(e.target.checked)}
							/>
							resolve
						</label>
					)}
					<button
						className="btn btn-xs btn-ghost"
						aria-haspopup="menu"
						aria-expanded={picker?.mode === "cite"}
						disabled={cites.length >= 10}
						onClick={e =>
							setPicker(
								picker?.mode === "cite"
									? null
									: { mode: "cite", anchor: e.currentTarget },
							)
						}
						title="Cite a message this builds on (up to 10)"
					>
						↗ Cite{cites.length ? ` ${cites.length}` : ""}
					</button>
					{kind === "decision" && (
						<button
							className="btn btn-xs btn-ghost"
							aria-haspopup="menu"
							aria-expanded={picker?.mode === "supersede"}
							onClick={e =>
								setPicker(
									picker?.mode === "supersede"
										? null
										: { mode: "supersede", anchor: e.currentTarget },
								)
							}
							title="Replace an earlier decision: it is marked SUPERSEDED"
						>
							Supersedes…
						</button>
					)}
					<button
						className="btn btn-xs urgent-toggle"
						aria-pressed={urgent}
						onClick={() => setUrgent(!urgent)}
						title="Urgent: wakes recipients and sorts first"
					>
						{urgent ? "▲ urgent" : "△ urgent"}
					</button>
					<span className="hint">
						<kbd>↵</kbd> send <kbd>⇧↵</kbd> newline
					</span>
					<button
						className="btn btn-xs btn-primary"
						onClick={submit}
						disabled={!body.trim() || !props.roomId}
					>
						Send
					</button>
				</div>
			</div>
		</div>
	)
}

function RefChip(props: {
	m: MessageView | undefined
	id: string
	prefix: "cites" | "supersedes"
	onRemove: () => void
}) {
	const label = props.m ? oneLine(props.m, 60) : shortId(props.id)
	return (
		<span className={"cite-chip" + (props.prefix === "supersedes" ? " sup" : "")}>
			<span className="dim">{props.prefix === "supersedes" ? "supersedes" : "↗"}</span>
			{props.m && <KindBadge kind={props.m.kind} />}
			<span className="ell">{label}</span>
			<button
				type="button"
				aria-label={`Remove ${props.prefix === "supersedes" ? "supersedes" : "cite"}: ${label}`}
				onClick={props.onRemove}
			>
				×
			</button>
		</span>
	)
}

/** Pick a message to cite, or a live decision to supersede. */
export function MessagePicker(props: {
	anchor: HTMLElement
	mode: "cite" | "supersede"
	exclude: string[]
	onPick: (id: string) => void
	onClose: () => void
}) {
	const [q, setQ] = useState("")
	const messages = useStore(s => s.messages)
	const now = useStore(s => s.now)
	const list = useMemo(() => {
		const needle = q.trim().toLowerCase()
		return Object.values(messages)
			.filter(
				m =>
					!m.id.startsWith("local:") &&
					!isWithdrawn(m) &&
					!props.exclude.includes(m.id) &&
					(props.mode === "cite" || (m.kind === "decision" && !m.supersededBy)),
			)
			.filter(
				m =>
					!needle ||
					(m.title ?? "").toLowerCase().includes(needle) ||
					m.body.toLowerCase().includes(needle) ||
					m.author.handle.toLowerCase().includes(needle) ||
					m.id.startsWith(needle),
			)
			.sort((a, b) => b.seq - a.seq)
			.slice(0, 30)
	}, [messages, q, props.mode, props.exclude])
	const label = props.mode === "cite" ? "Cite a message" : "Supersede a decision"
	return (
		<Popover anchor={props.anchor} onClose={props.onClose} label={label}>
			<div className="picker">
				<div className="menu-label">{label}</div>
				<input
					className="input picker-input"
					value={q}
					onChange={e => setQ(e.target.value)}
					placeholder={props.mode === "cite" ? "Search title, text, @author, id" : "Search decisions"}
					aria-label={label}
					onKeyDown={e => {
						if (e.key === "Enter" && list[0]) {
							e.preventDefault()
							props.onPick(list[0].id)
						}
					}}
				/>
				{list.map(m => (
					<button
						key={m.id}
						className="menu-item pick-item"
						role="menuitem"
						onClick={() => props.onPick(m.id)}
					>
						<KindBadge kind={m.kind} />
						<span className="ell grow">{oneLine(m)}</span>
						<span className="dim">@{m.author.handle}</span>
						<span className="mono dim">{age(m.createdAt, now)}</span>
					</button>
				))}
				{!list.length && (
					<div className="empty left">
						{props.mode === "cite" ? "No messages match." : "No live decisions to supersede."}
					</div>
				)}
			</div>
		</Popover>
	)
}
